import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Table, TableBody, TableRow, TableCell, TableHeader } from 'components/Table';
import cx from 'classnames';
import { Tag, Icon } from '@ontruck/orc';
import { palletsByCountry } from 'utils/journey';
import VehicleImage from 'components/VehicleImage';
import { VEHICLE_STATES } from 'components/CarrierDetails/Constants';
import LogCarrierNotes from 'components/LogCarrierNotes';
import { constants, getSpecificClientConfig } from 'redux/modules/client-config/selectors';
import { useSelector } from 'react-redux';

const VehicleList = (props) => {
  const { vehicles = [], onEditVehicle, className = '', isVehicleWidthHidden, t } = props;
  const isContainerLoad = useSelector((store) =>
    getSpecificClientConfig(store, constants.CONTAINER_LOAD)
  );

  const renderHeaders = () => {
    return (
      <TableHeader className="vehicle-list__header">
        <TableRow className="vehicle-list__header-row">
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell>{t.zipcode}</TableCell>
          {!isContainerLoad && <TableCell>{t.pallets}</TableCell>}
          <TableCell>{t.weight}</TableCell>
          <TableCell>{t.height}</TableCell>
          {!isVehicleWidthHidden && <TableCell>{t.width}</TableCell>}
          <TableCell></TableCell>
        </TableRow>
      </TableHeader>
    );
  };

  const renderStatusTag = (vehicle) => {
    if (vehicle.status === VEHICLE_STATES.not_active) {
      return <Tag text={t.not_active} skin="modernGrey" weight="bold" size="M" />;
    }
  };

  const renderMaxPallets = (vehicle) => {
    const pallet_type =
      (vehicle && vehicle.carrier && palletsByCountry[vehicle.carrier.country]) ||
      palletsByCountry.default;
    const palletType = vehicle.max_pallets ? `${vehicle.max_pallets} ${t[pallet_type]}` : null;

    return <span>{palletType}</span>;
  };

  const renderVehicleType = (vehicle) => {
    return (
      <span className="vehicle-list__type">
        {/* NEED: Vehicle type or platform */}
        <VehicleImage type={vehicle.vehicle_type} platform={vehicle} />
        {vehicle.container_length && vehicle.container_length > 0 ? (
          <span className="vehicle-list__type-text">{`${vehicle.container_length} ${t.meters}`}</span>
        ) : null}
      </span>
    );
  };

  const renderExtras = (vehicle) => {
    return (
      <span className="vehicle-list__extras">
        {vehicle.has_liftgate && (
          <span title="Liftgate">
            <Icon size="3rem" glyph="extras-liftgate" color="grey500" />
          </span>
        )}
        {vehicle.has_lateral_loading && (
          <span title="Side load">
            <Icon size="3rem" glyph="extras-sideload" color="grey500" />
          </span>
        )}
        {vehicle.has_superior_loading && (
          <span title="Retractable roof">
            <Icon size="3rem" glyph="extras-retractableroof" color="grey500" />
          </span>
        )}
      </span>
    );
  };

  const renderRows = () => {
    return vehicles.map((vehicle) => {
      return (
        <TableRow
          key={vehicle.id}
          className={cx('vehicle-list__row', {
            'vehicle-list__row--inactive': vehicle.status === VEHICLE_STATES.not_active,
          })}
          onRowClick={() => onEditVehicle(vehicle.id)}
          dataTestId="vehicleListRow"
        >
          <TableCell className="vehicle-list__cell vehicle-list__cell-name">
            {vehicle.plate_number}
          </TableCell>
          <TableCell className="vehicle-list__cell">{renderVehicleType(vehicle)}</TableCell>
          <TableCell className="vehicle-list__cell vehicle-list__cell-tag">
            {renderStatusTag(vehicle)}
          </TableCell>
          <TableCell>
            <LogCarrierNotes log={vehicle.status_log} id={vehicle.id} />
          </TableCell>
          <TableCell className="vehicle-list__cell">
            {vehicle.zipcode ? vehicle.zipcode : '-'}
          </TableCell>
          {!isContainerLoad && (
            <TableCell className="vehicle-list__cell">{renderMaxPallets(vehicle)}</TableCell>
          )}
          <TableCell className="vehicle-list__cell">{`${
            vehicle.max_weight ? `${vehicle.max_weight} ${t.kilos}` : '-'
          } `}</TableCell>
          <TableCell className="vehicle-list__cell">{`${
            vehicle.container_height ? `${vehicle.container_height} ${t.meters}` : '-'
          }`}</TableCell>
          {!isVehicleWidthHidden && (
            <TableCell className="vehicle-list__cell">{`${
              vehicle.container_width ? `${vehicle.container_width} ${t.meters}` : '-'
            }`}</TableCell>
          )}
          <TableCell className="vehicle-list__cell">{renderExtras(vehicle)}</TableCell>
        </TableRow>
      );
    });
  };

  return (
    <div className={className} data-testid="vehicleList">
      <Table className="vehicle-list">
        {renderHeaders()}
        <TableBody>{renderRows()}</TableBody>
      </Table>
    </div>
  );
};

VehicleList.propTypes = {
  vehicles: PropTypes.array,
  onEditVehicle: PropTypes.func,
  intl: PropTypes.any,
  className: PropTypes.string,
};

export default injectIntl(VehicleList);
